export type BudgetStatus = "over" | "near" | "safe" | "none";

export type BudgetInsightLike = {
  id: number;
  name: string;
  spent: number;
  percentage: number;
  budgetAmount?: number;
  usagePercent?: number;
  remaining?: number;
  status: BudgetStatus;
};

type Translator = (key: string, params?: Record<string, unknown>) => string;

type BudgetMessageOptions = {
  category: Pick<BudgetInsightLike, "budgetAmount" | "remaining" | "spent" | "status" | "usagePercent">;
  t: Translator;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
};

const formatUsageMultiple = (usagePercent: number) => {
  const multiple = Math.max(usagePercent, 0);

  if (multiple >= 3) {
    return `${Math.round(multiple)}x`;
  }

  const rounded = Math.round(multiple * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}x`;
};

export const getBudgetStatus = (spent: number, budget?: number): BudgetStatus => {
  if (!budget || budget <= 0) return "none";

  const usage = spent / budget;

  if (usage > 1) return "over";
  if (usage >= 0.8) return "near";
  return "safe";
};

const getBudgetOverage = (
  category: Pick<BudgetInsightLike, "remaining">,
) => Math.abs(Math.min(category.remaining ?? 0, 0));

export const getOverspendMessage = ({
  category,
  t,
  formatCurrency,
  formatPercent,
}: BudgetMessageOptions) => {
  const overage = getBudgetOverage(category);
  const overPercent = Math.max(0, (category.usagePercent ?? 0) - 1) * 100;

  return {
    primary: t("analytics.overBudgetHeadline", {
      amount: formatCurrency(overage),
    }),
    secondary: t("analytics.aboveLimitHeadlineCompact", {
      percent: formatPercent(overPercent),
    }),
  };
};

export const getBudgetUsageLabel = ({
  category,
  t,
  formatCurrency,
  formatPercent,
}: BudgetMessageOptions) => {
  if (!category.budgetAmount || category.usagePercent == null) {
    return {
      primary: t("analytics.noBudget"),
      secondary: t("analytics.noBudgetDetailShort"),
    };
  }

  if (category.status === "over") {
    const overage = getBudgetOverage(category);

    return {
      primary: t("analytics.humanOverBudget", {
        amount: formatCurrency(overage),
      }),
      secondary:
        category.usagePercent >= 1.15
          ? t("analytics.budgetUsageMultiple", {
            multiple: formatUsageMultiple(category.usagePercent),
          })
          : t("analytics.aboveLimitHeadlineCompact", {
            percent: formatPercent((category.usagePercent - 1) * 100),
          }),
    };
  }

  const remaining = Math.max(category.remaining ?? 0, 0);

  if (remaining <= 0.01) {
    return {
      primary: t("analytics.usedFullBudget"),
      secondary: t("analytics.budgetUsageValue", {
        percent: formatPercent(category.usagePercent * 100),
      }),
    };
  }

  return {
    primary: t("analytics.remainingBudgetShort", {
      amount: formatCurrency(remaining),
    }),
    secondary: t("analytics.budgetUsageValue", {
      percent: formatPercent(category.usagePercent * 100),
    }),
  };
};
